import { supabase } from '@/core/database/supabase';
import type { AuditLogEntry, AuditFilters, AuditStats } from '../types/audit-logs.types';

export const AuditLogsService = {
  /**
   * Danh sách nhật ký thao tác
   */
  async getLogs(companyId: string, filters: AuditFilters = {}): Promise<{ data: AuditLogEntry[], total: number }> {
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 20;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('audit_logs')
      .select(`
        *,
        actor:user_profiles!audit_logs_actor_user_id_fkey(id, full_name, avatar_path),
        project:projects(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.moduleCode) query = query.eq('module_code', filters.moduleCode);
    if (filters.entityType) query = query.eq('entity_type', filters.entityType);
    if (filters.entityId) query = query.eq('entity_id', filters.entityId);
    if (filters.actorId) query = query.eq('actor_user_id', filters.actorId);
    if (filters.startDate) query = query.gte('created_at', filters.startDate);
    if (filters.endDate) query = query.lte('created_at', filters.endDate);
    if (filters.search) query = query.ilike('summary', `%${filters.search}%`);

    const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) throw error;
    return {
      data: (data || []) as AuditLogEntry[],
      total: count || 0
    };
  },

  /**
   * Thống kê KPI từ dữ liệu thật
   */
  async getStats(companyId: string, filters: Pick<AuditFilters, 'startDate' | 'endDate'> = {}): Promise<AuditStats> {
    let query = supabase
      .from('audit_logs')
      .select('actor_user_id, entity_id, action_code', { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.startDate) query = query.gte('created_at', filters.startDate);
    if (filters.endDate) query = query.lte('created_at', filters.endDate);

    const { data, count, error } = await query;
    if (error) throw error;

    const rows = data || [];
    const uniqueUsers = new Set(rows.map(r => r.actor_user_id).filter(Boolean)).size;
    const uniqueEntities = new Set(rows.map(r => r.entity_id).filter(Boolean)).size;
    const failedCount = rows.filter(r =>
      typeof r.action_code === 'string' && (r.action_code.includes('fail') || r.action_code.includes('error') || r.action_code.includes('denied'))
    ).length;

    return {
      total: count || 0,
      uniqueUsers,
      successCount: (count || 0) - failedCount,
      failedCount,
      uniqueEntities,
    };
  },

  /**
   * Ghi nhật ký mới (dùng trong các service khác)
   */
  async logAction(entry: Omit<AuditLogEntry, 'id' | 'created_at' | 'actor' | 'project'>): Promise<void> {
    const { error } = await supabase
      .from('audit_logs')
      .insert([entry]);

    if (error) throw error;
  }
};

